import * as React from "react";
import { useContext, useState, useEffect } from "react";
import ky, { HTTPError } from "ky";
import { parseError, getStandardHeaders, ApiError } from "../../apiUtils";
import { UserContext } from "../../crud/user/context";
import { BaseAccount } from "../../crud/account/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useQueryClient } from "react-query";

enum PatchApiError {
  InvalidAccount = "InvalidAccount",
  AlreadyExists = "AlreadyExists",
}

type PatchUserResult =
  | { error: undefined; defaultAccount: BaseAccount }
  | { error: ApiError | PatchApiError };

const patchUser = async (
  defaultAccount: BaseAccount,
): Promise<PatchUserResult> => {
  try {
    const result = await ky.patch(`/api/v1/users/me/`, {
      json: {
        default_account: defaultAccount.id,
      },
      headers: getStandardHeaders(),
    });
    const body = await result.json<any>();
    return { error: undefined, defaultAccount: body.default_account };
  } catch (err) {
    if (err instanceof HTTPError) {
      if (err?.response?.status === 409) {
        return { error: PatchApiError.AlreadyExists };
      }
      if (err?.response?.status >= 400) {
        const body = await err.response.json();
        if (body.default_account[0].startsWith("User ")) {
          return { error: PatchApiError.InvalidAccount };
        }
      }
    }
    return { error: parseError(err) };
  }
};

export const DefaultAccountForm = (): React.ReactElement => {
  const { user } = useContext(UserContext);
  const { memberships } = useContext(UserContext);
  const [defaultWorkspace, setDefaultWorkspace] = useState(
    user.defaultAccount.slug,
  );
  const queryClient = useQueryClient();

  // Sync local state with global user context when it changes
  useEffect(() => {
    setDefaultWorkspace(user.defaultAccount.slug);
  }, [user.defaultAccount.slug]);

  const handleAccountChange = async (accountSlug: string) => {
    const membership = memberships.find((m) => m.account.slug === accountSlug);
    if (membership === undefined) {
      // Invalid account selected - could show a toast notification here
      return;
    }

    const result = await patchUser(membership.account);
    if (result.error) {
      // Failed to update default account - could show a toast notification here
    } else {
      setDefaultWorkspace(accountSlug);
      // Invalidate user queries to refresh the global context
      queryClient.invalidateQueries();
    }
  };

  return (
    <Select value={defaultWorkspace} onValueChange={handleAccountChange}>
      <SelectTrigger size="sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {memberships.map((membership) => (
          <SelectItem
            key={membership.account.slug}
            value={membership.account.slug}
          >
            {membership.account.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
